import React, {FC, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {
  createUnitOfMeasure,
  deleteUnitOfMeasure,
  getUnitsOfMeasure,
  updateUnitOfMeasure,
} from '../core/_requests'
import {UnitOfMeasure} from '../core/_models'

type UomForm = {code: string; name: string; symbol: string; active: boolean}

const emptyForm = (): UomForm => ({code: '', name: '', symbol: '', active: true})

type Props = {
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
}

const UomLibraryPanel: FC<Props> = ({canCreate, canUpdate, canDelete}) => {
  const [rows, setRows] = useState<UnitOfMeasure[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<UomForm>(emptyForm)
  const [editing, setEditing] = useState<UnitOfMeasure | null>(null)
  const [showForm, setShowForm] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      setRows(await getUnitsOfMeasure(true))
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load units of measurement')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm())
    setShowForm(true)
  }

  const openEdit = (row: UnitOfMeasure) => {
    setEditing(row)
    setForm({code: row.code || '', name: row.name || '', symbol: row.symbol || '', active: row.active !== false})
    setShowForm(true)
  }

  const submit = async () => {
    const name = form.name.trim()
    if (!name) {
      toast.error('UOM name is required')
      return
    }
    setSaving(true)
    const payload = {
      code: form.code.trim() || undefined,
      name,
      symbol: form.symbol.trim() || undefined,
      active: form.active,
    }
    try {
      if (editing) {
        await updateUnitOfMeasure(editing.id, payload)
        toast.success('Unit of measurement updated')
      } else {
        await createUnitOfMeasure(payload)
        toast.success('Unit of measurement created')
      }
      setShowForm(false)
      await load()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save unit of measurement')
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async (row: UnitOfMeasure) => {
    if (!window.confirm(`Deactivate ${row.name}?`)) return
    try {
      await deleteUnitOfMeasure(row.id)
      toast.success('Unit of measurement deactivated')
      await load()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to deactivate unit of measurement')
    }
  }

  return (
    <div className='h-100 d-flex flex-column gap-3'>
      <div className='d-flex justify-content-between align-items-start'>
        <div>
          <h5 className='mb-1 fw-bold text-dark'>Unit of Measurement Library</h5>
          <div className='text-muted small'>Units available for procurement and inventory records.</div>
        </div>
        {canCreate && <button className='btn btn_primary btn-sm' onClick={openCreate}>Add Unit</button>}
      </div>

      <div className='card shadow-sm border h-100'>
        <div className='table-responsive' style={{maxHeight: '100%'}}>
          <table className='table table-striped table-bordered align-middle mb-0'>
            <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}>
              <tr className='fw-semibold text-uppercase text-muted fs-8'>
                <th>UOM Code</th>
                <th>UOM Name</th>
                <th>Symbol</th>
                <th>Status</th>
                <th>ID</th>
                {(canUpdate || canDelete) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canUpdate || canDelete ? 6 : 5} className='text-center text-muted py-4'>Loading units...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={canUpdate || canDelete ? 6 : 5} className='text-center text-muted py-4'>No units of measurement found.</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id}>
                  <td className='fw-semibold text-dark'>{row.code}</td>
                  <td>{row.name}</td>
                  <td>{row.symbol || '-'}</td>
                  <td><span className={`badge ${row.active === false ? 'badge-light-danger' : 'badge-light-success'}`}>{row.active === false ? 'Inactive' : 'Active'}</span></td>
                  <td className='text-muted'>{row.id}</td>
                  {(canUpdate || canDelete) && <td><div className='d-flex gap-1'>
                    {canUpdate && <button className='btn btn-sm btn-light-warning' onClick={() => openEdit(row)}>Edit</button>}
                    {canDelete && row.active !== false && <button className='btn btn-sm btn-light-danger' onClick={() => void deactivate(row)}>Deactivate</button>}
                  </div></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className='modal d-block' role='dialog' aria-modal='true' style={{background: 'rgba(0, 0, 0, 0.45)'}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editing ? 'Edit Unit of Measurement' : 'Add Unit of Measurement'}</h5>
                <button type='button' className='btn-close' onClick={() => setShowForm(false)} disabled={saving} />
              </div>
              <div className='modal-body'>
                <div className='mb-3'><label className='form-label'>UOM Code</label><input className='form-control' value={form.code} onChange={(event) => setForm((value) => ({...value, code: event.target.value}))} /></div>
                <div className='mb-3'><label className='form-label'>UOM Name *</label><input className='form-control' value={form.name} onChange={(event) => setForm((value) => ({...value, name: event.target.value}))} /></div>
                <div className='mb-3'><label className='form-label'>Symbol</label><input className='form-control' value={form.symbol} onChange={(event) => setForm((value) => ({...value, symbol: event.target.value}))} /></div>
                {editing && <label className='form-check form-switch'><input className='form-check-input' type='checkbox' checked={form.active} onChange={(event) => setForm((value) => ({...value, active: event.target.checked}))} /><span className='form-check-label'>Active</span></label>}
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
                <button type='button' className='btn btn_primary' onClick={() => void submit()} disabled={saving}>{saving ? 'Saving...' : 'Save Unit'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UomLibraryPanel
